import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export type ExcelColumn = {
  header: string;
  key: string;
  /** When set, cell values are written as Excel numbers with this format (e.g. `#,##0.00`). */
  numFmt?: string;
};

export function parseExcelNumericAmount(raw: unknown): number | null {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (raw == null || raw === "" || raw === "—") return null;
  const text = String(raw).trim();
  if (!text) return null;

  // Keep digits, separators, and sign; drop currency codes and symbols.
  let cleaned = text.replace(/[^\d.,\-+]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === "+") return null;

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  if (lastComma >= 0 && lastDot >= 0) {
    if (lastComma > lastDot) {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      cleaned = cleaned.replace(/,/g, "");
    }
  } else if (lastComma >= 0) {
    cleaned = cleaned.replace(",", ".");
  }

  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

export async function exportRowsToExcel(
  rows: object[],
  columns: ExcelColumn[],
  options: { sheetName?: string; fileNamePrefix: string },
) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "MoneyEU CRM";
  const sheet = workbook.addWorksheet(options.sheetName ?? "Export");

  sheet.columns = columns.map((col) => ({
    header: col.header,
    key: col.key,
    style: col.numFmt ? { numFmt: col.numFmt } : undefined,
  }));

  // Write by column order. addRow(object) skips keys that clash with ExcelJS Row
  // properties (notably `status`), which left Status columns empty.
  rows.forEach((row) => {
    const record = row as Record<string, unknown>;
    sheet.addRow(
      columns.map((col) => {
        const raw = record[col.key];
        if (!col.numFmt) return raw;
        const numeric = parseExcelNumericAmount(raw);
        return numeric ?? raw;
      }),
    );
  });

  columns.forEach((col, index) => {
    if (!col.numFmt) return;
    const excelCol = sheet.getColumn(index + 1);
    excelCol.numFmt = col.numFmt;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${options.fileNamePrefix}-${Date.now()}.xlsx`);
}
